import { useEffect, useState } from "react";
import { Briefcase, Users, Rss, Clock } from "lucide-react";
import { MISSING_ADDRESS_QUERY, OFFICE_NOT_LOCATED_QUERY, NAICS_PENDING_QUERY } from "features/crm/api/CrmApi";
import { AdminDashboardStats, getAdminDashboardStats } from "features/dashboard/api/DashboardApi";
import { StatCard, StatusBadgeRow, AlertBanner, JOB_PIPELINE_KEYS, PIPELINE_LABELS } from "features/dashboard/ui/shared";
import TmLeaderboard from "features/dashboard/ui/TmLeaderboard";
import DeadlineRadar from "features/dashboard/ui/DeadlineRadar";
import DueTodayCard from "features/dashboard/ui/DueTodayCard";
import MeetingsOverdueCard from "features/dashboard/ui/MeetingsOverdueCard";

function Spinner() {
  return <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminDashboardStats()
      .then(setStats)
      .catch((e) => setError(e.response?.data?.detail || "Failed to load dashboard stats"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10">
        <Spinner />
        <span className="text-sm text-muted-foreground font-inter">Loading dashboard…</span>
      </div>
    );
  }

  if (error || !stats) {
    return <p className="text-sm text-destructive font-inter">{error || "No data available."}</p>;
  }

  const todayTotal = stats.today.linkedin_alerts + stats.today.theirstack_jobs + stats.today.watchlist_leads;
  const staleTotal = stats.stale_new_org_wide.linkedin_alerts + stats.stale_new_org_wide.theirstack_jobs + stats.stale_new_org_wide.watchlist_leads;
  const unassignedTotal = stats.unassigned_hiring.companies + stats.unassigned_hiring.contacts;
  const anySilentPipeline = JOB_PIPELINE_KEYS.some((key) => stats.silent_pipelines[key]);
  const missingAddress = stats.companies_missing_address ?? 0;
  const notLocated = stats.companies_office_not_located ?? 0;
  const naicsPending = stats.companies_naics_pending ?? 0;
  const alertRowCount =
    (missingAddress > 0 ? 1 : 0) +
    (notLocated > 0 ? 1 : 0) +
    (naicsPending > 0 ? 1 : 0) +
    (stats.unassigned_hiring.contacts > 0 ? 1 : 0) +
    (stats.unassigned_hiring.companies > 0 ? 1 : 0) +
    JOB_PIPELINE_KEYS.filter((key) => stats.stale_new_org_wide[key] > 0).length +
    (stats.inactive_tm_count > 0 ? 1 : 0) +
    JOB_PIPELINE_KEYS.filter((key) => stats.silent_pipelines[key]).length;

  return (
    <div className="space-y-8">
      {/* Alerts */}
      {(missingAddress > 0 || notLocated > 0 || naicsPending > 0 || unassignedTotal > 0 || staleTotal > 0 || stats.inactive_tm_count > 0 || anySilentPipeline) && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-base font-bold text-foreground font-inter">Needs Attention</h2>
            <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold font-inter">
              {alertRowCount}
            </span>
          </div>
          <div className="space-y-2">
            {missingAddress > 0 && (
              <AlertBanner to={`/admin/crm/companies?${MISSING_ADDRESS_QUERY}`} linkLabel="Fix Now" variant="action">
                <span className="font-semibold">{missingAddress}</span> Compan{missingAddress === 1 ? "y has" : "ies have"} no address set — research and fix {missingAddress === 1 ? "its" : "their"} address.
              </AlertBanner>
            )}
            {notLocated > 0 && (
              <AlertBanner to={`/admin/crm/companies?${OFFICE_NOT_LOCATED_QUERY}`} linkLabel="Fix Now" variant="action">
                <span className="font-semibold">{notLocated}</span> Compan{notLocated === 1 ? "y has" : "ies have"} an office that could not be placed on the map — add a city and state, or a zip.
              </AlertBanner>
            )}
            {naicsPending > 0 && (
              <AlertBanner to={`/admin/crm/companies?${NAICS_PENDING_QUERY}`} linkLabel="Review" variant="action">
                <span className="font-semibold">{naicsPending}</span> Compan{naicsPending === 1 ? "y has" : "ies have"} NAICS suggestions waiting for approval — not used by any RFP check until approved.
              </AlertBanner>
            )}
            {stats.unassigned_hiring.contacts > 0 && (
              <AlertBanner to="/admin/hiring-contacts" linkLabel="Assign Contacts" variant="action">
                <span className="font-semibold">{stats.unassigned_hiring.contacts}</span> Hiring Contact{stats.unassigned_hiring.contacts === 1 ? "" : "s"} unassigned — assign to a Technical Manager.
              </AlertBanner>
            )}
            {stats.unassigned_hiring.companies > 0 && (
              <AlertBanner to="/admin/hiring-contacts" linkLabel="Assign Companies" variant="action">
                <span className="font-semibold">{stats.unassigned_hiring.companies}</span> Compan{stats.unassigned_hiring.companies === 1 ? "y" : "ies"} unassigned — assign to a Technical Manager.
              </AlertBanner>
            )}
            {JOB_PIPELINE_KEYS.map((key) => (
              stats.stale_new_org_wide[key] > 0 && (
                <AlertBanner key={key} to="/admin/job-board" linkLabel={`View ${PIPELINE_LABELS[key]}`} variant="urgent">
                  <span className="font-semibold">{stats.stale_new_org_wide[key]}</span> {PIPELINE_LABELS[key]} stuck in New status for {stats.stale_new_days_org}+ days.
                </AlertBanner>
              )
            ))}
            {stats.inactive_tm_count > 0 && (
              <AlertBanner variant="system">
                <span className="font-semibold">{stats.inactive_tm_count}</span> Technical Manager{stats.inactive_tm_count === 1 ? "" : "s"} with no activity in {stats.inactive_tm_days}+ days — see the table below.
              </AlertBanner>
            )}
            {JOB_PIPELINE_KEYS.map((key) => (
              stats.silent_pipelines[key] && (
                <AlertBanner key={`silent-${key}`} variant="system">
                  No new {PIPELINE_LABELS[key]} ingested in {stats.silent_pipeline_days}+ days — check the n8n workflow feeding this pipeline.
                </AlertBanner>
              )
            ))}
          </div>
        </div>
      )}

      {/* Today's new jobs */}
      <div>
        <h2 className="text-base font-bold text-foreground font-inter mb-3">Today — New Jobs</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Clock} label="Total Today" value={todayTotal} hero />
          <StatCard icon={Briefcase} label="LinkedIn Alerts" value={stats.today.linkedin_alerts} />
          <StatCard icon={Rss} label="Other Jobs" value={stats.today.theirstack_jobs} />
          <StatCard icon={Users} label="Watchlist Leads" value={stats.today.watchlist_leads} />
        </div>
      </div>

      {/* RFP deadlines due soon */}
      <DeadlineRadar />
      <DueTodayCard />
      <MeetingsOverdueCard />

      {/* Status funnel */}
      <div>
        <h2 className="text-base font-bold text-foreground font-inter mb-3">Org-Wide Status Funnel</h2>
        <div className="rounded-2xl border border-border bg-surface px-4">
          {JOB_PIPELINE_KEYS.map((key) => (
            <StatusBadgeRow key={key} pipelineKey={key} counts={stats.status_funnel[key]} />
          ))}
        </div>
      </div>

      {/* TM leaderboard */}
      <TmLeaderboard />
    </div>
  );
}
